/**
 * LegalPage — shared shell for the public legal pages (/privacy, /terms).
 *
 * Same public-page conventions as Status.tsx: brand tokens, logo header with
 * language + theme switchers, single centered column. Content lives entirely
 * in the `legal` i18n namespace (4 languages) — this component only renders
 * the section list it receives.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { LogoIcon } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Flag } from '@/components/LanguageSelector';
import { languages, type LanguageCode } from '@/i18n';
import { cn } from '@/lib/utils';

export interface LegalSection {
  h: string;
  b: string;
}

/** Compact flag switcher (same pattern as Landing/Status public pages). */
function LegalLangSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = (i18n.language?.startsWith('pt')
    ? 'pt-BR'
    : (i18n.language?.split('-')[0] || 'pt-BR')) as LanguageCode;
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={t('landing:a11y.language')}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <Flag code={current} className="h-4 w-6 rounded-sm shadow-sm" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1.5 z-50 w-44 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-xl p-1">
            {(Object.keys(languages) as LanguageCode[]).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                  code === current
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                    : 'text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]',
                )}
              >
                <Flag code={code} className="h-4 w-6 rounded-sm shadow-sm" />
                <span>{languages[code].name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function LegalPage({
  title,
  intro,
  sections,
  anchors = [],
}: {
  title: string;
  intro: string;
  sections: LegalSection[];
  /** Optional element ids, aligned by index with `sections` (e.g. "cookies"). */
  anchors?: (string | undefined)[];
}) {
  const { t } = useTranslation();

  // Deep-link support (/privacy#cookies): scroll to the anchored section.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden relative text-[var(--color-text)]"
      style={{ background: 'var(--color-base)' }}
    >
      <div className="relative z-10 max-w-[860px] mx-auto px-5 sm:px-8 pt-0 pb-24">
        {/* ── Top nav ── */}
        <header className="flex items-center justify-between py-5 mb-6">
          <Link to="/" className="flex items-center gap-2">
            <LogoIcon size="sm" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <span className="font-logo text-xl text-[var(--color-text)]">my360</span>
          </Link>
          <div className="flex items-center gap-2">
            <LegalLangSwitcher />
            <ThemeToggle />
          </div>
        </header>

        {/* ── Title ── */}
        <div className="mb-10">
          <h1 className="text-display text-3xl sm:text-4xl font-semibold tracking-tighter mb-3">
            {title}
          </h1>
          <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-widest font-semibold">
            {t('legal:lastUpdated')}
          </p>
        </div>

        {/* ── Intro ── */}
        <p className="text-base text-[var(--color-text-muted)] leading-relaxed mb-10">{intro}</p>

        {/* ── Sections ── */}
        <div className="space-y-10">
          {sections.map((s, i) => (
            <section key={s.h} id={anchors[i]} className="scroll-mt-24">
              <h2 className="text-lg sm:text-xl font-semibold tracking-tight mb-3">{s.h}</h2>
              {s.b.split('\n\n').map((para, j) => (
                <p
                  key={j}
                  className="text-sm sm:text-[15px] text-[var(--color-text-muted)] leading-relaxed mb-3 last:mb-0"
                >
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>

        {/* ── Footer ── */}
        <div className="mt-14 pt-8 border-t border-[var(--color-border)]">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('legal:backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
